/**
 * HeadingElement — F12.H T12.H.5.
 *
 * Nagłówek tekstowy (h1–h6) jako element dekoracyjny w formularzu.
 * Nie jest polem — nie ma wartości, służy do organizacji wizualnej.
 *
 * Settings shape:
 *   { text: string, level?: 1|2|3|4|5|6, align?: 'left'|'center'|'right' }
 */

import { Dropdown } from '@/components/Dropdown';
import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';
import { Input } from '@/components/Input';

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
type HeadingAlign = 'left' | 'center' | 'right';

const LEVEL_CLASSES: Record<HeadingLevel, string> = {
  1: 'text-3xl font-bold',
  2: 'text-2xl font-bold',
  3: 'text-xl font-semibold',
  4: 'text-lg font-semibold',
  5: 'text-base font-semibold',
  6: 'text-sm font-semibold uppercase tracking-wider',
};

const ALIGN_CLASSES: Record<HeadingAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

export const HeadingElement: LayoutElementDefinition = {
  type: 'heading',
  category: 'tresc',
  isContainer: false,
  label: 'Nagłówek',
  icon: 'Heading',
  defaultSettings: () => ({
    text: 'Nagłówek',
    level: 2,
    align: 'left',
  }),
  propertiesEditor: ({ settings, onChange }) => {
    const text = String(settings.text ?? '');
    const level = (settings.level as HeadingLevel) ?? 2;
    const align = (settings.align as HeadingAlign) ?? 'left';
    return (
      <div className="space-y-3" data-testid="heading-properties-editor">
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">Tekst</label>
          <Input
            type="text"
            value={text}
            onChange={(e) => onChange({ text: e.target.value })}
            className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm focus:border-accent-500 focus:ring-1 focus:ring-accent-500"
            data-testid="heading-prop-text"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">Poziom</label>
          <Dropdown
            value={String(level)}
            onChange={(v) => onChange({ level: Number(v) as HeadingLevel })}
            className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
            data-testid="heading-prop-level"
            options={[1, 2, 3, 4, 5, 6].map((l) => ({ value: String(l), label: `H${l}` }))}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">Wyrównanie</label>
          <Dropdown
            value={align}
            onChange={(v) => onChange({ align: v as HeadingAlign })}
            className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
            data-testid="heading-prop-align"
            options={[
              { value: 'left', label: 'Do lewej' },
              { value: 'center', label: 'Wyśrodkowane' },
              { value: 'right', label: 'Do prawej' },
            ]}
          />
        </div>
      </div>
    );
  },
  render: ({ element, mode }) => {
    const settings = (element.settings ?? {}) as {
      text?: string;
      level?: HeadingLevel;
      align?: HeadingAlign;
    };
    const level = (settings.level ?? 2) as HeadingLevel;
    const align = settings.align ?? 'left';
    const text = settings.text || (mode === 'builder' ? '(pusty nagłówek)' : '');
    const className = `${LEVEL_CLASSES[level]} ${ALIGN_CLASSES[align]} text-text-primary`;

    const Tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    return (
      <Tag
        className={className}
        data-testid={`heading-element-${element.id}`}
        data-element-type="heading"
      >
        {text}
      </Tag>
    );
  },
};
